import type { Frequentie, GesprekMeta, TaakVelden, TerugkerendeTaak, TerugkerendVelden } from '../../gedeeld/types'

/* ===========================================================================
 *  Woorden en datums
 *
 *  Eén plek voor hoe iets heet en hoe een tijd eruitziet. Een terugkerende
 *  taak heet op de kaart in het gesprek precies zoals in de lijst met taken
 *  ("Elke werkdag om 08:00"); anders lijken het twee verschillende dingen.
 * =========================================================================== */

/* ------------------------------------------------------------------ *
 *  Datums
 * ------------------------------------------------------------------ */

const dag = new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'short' })
const dagJaar = new Intl.DateTimeFormat('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' })
const volledig = new Intl.DateTimeFormat('nl-NL', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })
const langeDatum = new Intl.DateTimeFormat('nl-NL', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })
const tijd = new Intl.DateTimeFormat('nl-NL', { hour: '2-digit', minute: '2-digit' })
const weekdag = new Intl.DateTimeFormat('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' })

export const UUR = 3600000
export const DAG = 86400000

export function datum(t: number | null | undefined): string {
  if (!t) return ''
  const d = new Date(t)
  return d.getFullYear() === new Date().getFullYear() ? dag.format(d) : dagJaar.format(d)
}

/** "wo 1 okt 14:05": voor een tijdstempel bij aanwijzen. */
export function datumTijd(t: number | null | undefined): string {
  if (!t) return ''
  return volledig.format(new Date(t))
}

export function kloktijd(t: number): string {
  return tijd.format(new Date(t))
}

export function startVanDag(t: number): number {
  const d = new Date(t)
  d.setHours(0, 0, 0, 0)
  return d.getTime()
}

/** Hoeveel kalenderdagen t vóór vandaag ligt: 0 is vandaag, 1 gisteren. */
function dagenGeleden(t: number): number {
  return Math.round((startVanDag(Date.now()) - startVanDag(t)) / DAG)
}

/** "zojuist", "12 min geleden", "gisteren", of een datum. */
export function geleden(t: number | null | undefined): string {
  if (!t) return ''
  const s = Math.round((Date.now() - t) / 1000)
  if (s < 45) return 'zojuist'
  if (s < 3600) return `${Math.round(s / 60)} min geleden`
  const dagen = dagenGeleden(t)
  if (dagen === 0) return `${Math.round(s / 3600)} uur geleden`
  if (dagen === 1) return 'gisteren'
  if (dagen < 7) return `${dagen} dagen geleden`
  return datum(t)
}

/** Vooruit: "over 12 min", "vandaag 14:00", "morgen 08:00", "ma 6 okt 08:00". */
export function straks(t: number | null | undefined): string {
  if (!t) return ''
  const s = Math.round((t - Date.now()) / 1000)
  if (s < 0) return geleden(t)
  if (s < 60) return 'zo meteen'
  if (s < 3600) return `over ${Math.round(s / 60)} min`
  const dagen = -dagenGeleden(t)
  if (dagen === 0) return `vandaag ${kloktijd(t)}`
  if (dagen === 1) return `morgen ${kloktijd(t)}`
  return `${weekdag.format(new Date(t))} ${kloktijd(t)}`
}

/** "12 s", "1 min 20 s": hoe lang iets duurde. */
export function duur(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return ''
  if (ms < 1000) return `${Math.max(0, Math.round(ms))} ms`
  const s = Math.round(ms / 1000)
  if (s < 60) return `${s} s`
  const m = Math.floor(s / 60)
  return s % 60 ? `${m} min ${s % 60} s` : `${m} min`
}

/** Voor <input type="date">: yyyy-mm-dd in de eigen tijdzone. */
export function vandaagInvoer(plusDagen = 0): string {
  const d = new Date(Date.now() + plusDagen * DAG)
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

/** "2026-10-03" wordt "vrijdag 3 oktober 2026"; om 12:00 gelezen zodat een tijdzone hem niet verschuift. */
export function invoerDatumLang(w: string | null | undefined): string {
  if (!w || !/^\d{4}-\d{2}-\d{2}$/.test(w)) return ''
  return langeDatum.format(new Date(w + 'T12:00:00'))
}

/* ------------------------------------------------------------------ *
 *  De gesprekken in groepen, zoals elke chat-app dat doet
 * ------------------------------------------------------------------ */

export interface Groep { titel: string; gesprekken: GesprekMeta[] }

export function groepeer(lijst: GesprekMeta[]): Groep[] {
  const vast: GesprekMeta[] = []
  const bakken: Groep[] = [
    { titel: 'Vandaag', gesprekken: [] },
    { titel: 'Gisteren', gesprekken: [] },
    { titel: 'Afgelopen 7 dagen', gesprekken: [] },
    { titel: 'Afgelopen 30 dagen', gesprekken: [] },
    { titel: 'Eerder', gesprekken: [] },
  ]
  const op = [...lijst].sort((a, b) => b.bijgewerkt - a.bijgewerkt)
  for (const g of op) {
    if (g.vastgezet) { vast.push(g); continue }
    const d = dagenGeleden(g.bijgewerkt)
    const bak = d <= 0 ? 0 : d === 1 ? 1 : d < 7 ? 2 : d < 30 ? 3 : 4
    bakken[bak]!.gesprekken.push(g)
  }
  return [{ titel: 'Vastgezet', gesprekken: vast }, ...bakken].filter((g) => g.gesprekken.length)
}

/* ------------------------------------------------------------------ *
 *  Mensen
 * ------------------------------------------------------------------ */

export function initialen(naam: string | null | undefined): string {
  if (!naam) return '?'
  const delen = naam.trim().split(/\s+/).filter(Boolean)
  const eerste = delen[0]?.[0] ?? '?'
  const laatste = delen.length > 1 ? delen[delen.length - 1]?.[0] ?? '' : ''
  return (eerste + laatste).toUpperCase()
}

export const voornaam = (naam: string | null | undefined) => (naam ?? '').trim().split(/\s+/)[0] ?? ''

/** Een vaste kleur per persoon, uit de naam, zodat iemand overal dezelfde is. */
export function persoonKleur(sleutel: string | null | undefined): string {
  const lijst = ['info', 'ok', 'paars', 'oranje', 'brand', 'danger', 'warn']
  let h = 0
  for (const c of sleutel ?? '') h = (h * 31 + c.charCodeAt(0)) >>> 0
  return lijst[h % lijst.length]!
}

/*
 * De rollen die het dashboard nog kent (ROLE_LABELS in src/lib/types.ts
 * daar): drie. De andere -- werknemer, leidinggevende, monteur, kassa en de
 * rest -- zijn eruit (migraties 0176, 0179, 0180). Een rol die hier niet
 * staat, krijgt gewoon zijn eigen naam met een hoofdletter.
 */
const ROLNAMEN: Record<string, string> = {
  management: 'Management',
  developer: 'Ontwikkelaar',
  administratie: 'Administratie',
}

/** De rollen kort, voor onder je naam: "Management · Financiën". */
export function rollenKort(rollen: string[]): string {
  const namen = rollen.map((r) => ROLNAMEN[r.toLowerCase()] ?? (r.charAt(0).toUpperCase() + r.slice(1).replace(/[_-]/g, ' ')))
  const uniek = [...new Set(namen)]
  return uniek.length > 2 ? `${uniek.slice(0, 2).join(' · ')} +${uniek.length - 2}` : uniek.join(' · ')
}

/** Goedemorgen of goedenavond, naar de klok van wie kijkt. */
export function begroeting(nu = new Date()): string {
  const u = nu.getHours()
  if (u < 6) return 'Goedenacht'
  if (u < 12) return 'Goedemorgen'
  if (u < 18) return 'Goedemiddag'
  return 'Goedenavond'
}

export const meervoud = (n: number, een: string, meer: string) => `${n} ${n === 1 ? een : meer}`

/* ------------------------------------------------------------------ *
 *  Taken uit een voorstel
 * ------------------------------------------------------------------ */

export const PRIORITEITEN: { key: TaakVelden['prioriteit']; label: string; kleur: string }[] = [
  { key: 'laag', label: 'Laag', kleur: 'ok' },
  { key: 'normaal', label: 'Normaal', kleur: 'info' },
  { key: 'hoog', label: 'Hoog', kleur: 'oranje' },
  { key: 'urgent', label: 'Urgent', kleur: 'danger' },
]

/* ------------------------------------------------------------------ *
 *  Terugkerende taken: het schema in gewone woorden
 * ------------------------------------------------------------------ */

export const FREQUENTIES: { key: Frequentie; label: string }[] = [
  { key: 'dagelijks', label: 'Elke dag' },
  { key: 'werkdagen', label: 'Werkdagen' },
  { key: 'wekelijks', label: 'Wekelijks' },
  { key: 'maandelijks', label: 'Maandelijks' },
  { key: 'eenmalig', label: 'Eén keer' },
]

/** 1 = maandag … 7 = zondag, zoals in het contract. */
export const DAGEN: { nr: number; kort: string; lang: string }[] = [
  { nr: 1, kort: 'ma', lang: 'maandag' },
  { nr: 2, kort: 'di', lang: 'dinsdag' },
  { nr: 3, kort: 'wo', lang: 'woensdag' },
  { nr: 4, kort: 'do', lang: 'donderdag' },
  { nr: 5, kort: 'vr', lang: 'vrijdag' },
  { nr: 6, kort: 'za', lang: 'zaterdag' },
  { nr: 7, kort: 'zo', lang: 'zondag' },
]

function opsomming(woorden: string[]): string {
  if (woorden.length <= 1) return woorden.join('')
  return `${woorden.slice(0, -1).join(', ')} en ${woorden[woorden.length - 1]}`
}

/** "Elke werkdag om 08:00", "Elke maandag en donderdag om 07:30", "Elke maand op de 1e om 09:00". */
export function schemaZin(v: Pick<TerugkerendVelden, 'frequentie' | 'tijd' | 'dagen' | 'dagVanMaand' | 'datum'>): string {
  const om = /^\d{1,2}:\d{2}$/.test(v.tijd ?? '') ? ` om ${v.tijd}` : ''
  switch (v.frequentie) {
    case 'dagelijks': return `Elke dag${om}`
    case 'werkdagen': return `Elke werkdag${om}`
    case 'wekelijks': {
      const dagen = [...new Set(v.dagen ?? [])].filter((d) => d >= 1 && d <= 7).sort((a, b) => a - b)
      if (!dagen.length) return `Wekelijks${om} (kies een dag)`
      if (dagen.length === 7) return `Elke dag${om}`
      if (dagen.join() === '1,2,3,4,5') return `Elke werkdag${om}`
      return `Elke ${opsomming(dagen.map((d) => DAGEN[d - 1]!.lang))}${om}`
    }
    case 'maandelijks':
      return v.dagVanMaand ? `Elke maand op de ${v.dagVanMaand}e${om}` : `Maandelijks${om} (kies een dag)`
    case 'eenmalig':
      return v.datum ? `Eén keer, op ${invoerDatumLang(v.datum)}${om}` : `Eén keer${om} (kies een datum)`
  }
}

export const LEGE_TERUGKEREND: TerugkerendVelden = {
  naam: '',
  opdracht: '',
  frequentie: 'werkdagen',
  tijd: '08:00',
  dagen: [1],
  dagVanMaand: 1,
  datum: null,
  melden: true,
  mailen: false,
}

/** Wat er niet klopt aan een schema, per veld; leeg = alles in orde. */
export function controleerTerugkerend(v: TerugkerendVelden): Partial<Record<keyof TerugkerendVelden, string>> {
  const f: Partial<Record<keyof TerugkerendVelden, string>> = {}
  if (!v.naam.trim()) f.naam = 'Geef de taak een naam, zodat je hem in de lijst terugvindt.'
  if (!v.opdracht.trim()) f.opdracht = 'Schrijf op wat Cowork moet uitzoeken.'
  else if (v.opdracht.trim().length < 8) f.opdracht = 'Dat is wel erg kort; schrijf het op zoals je het zou vragen.'
  if (!/^([01]\d|2[0-3]):[0-5]\d$/.test(v.tijd)) f.tijd = 'Een tijd als 08:00.'
  if (v.frequentie === 'wekelijks' && !v.dagen.length) f.dagen = 'Kies minstens één dag.'
  if (v.frequentie === 'maandelijks' && !(v.dagVanMaand && v.dagVanMaand >= 1 && v.dagVanMaand <= 28)) {
    f.dagVanMaand = 'Kies een dag van 1 tot en met 28.'
  }
  if (v.frequentie === 'eenmalig') {
    if (!v.datum) f.datum = 'Kies een datum.'
    else if (v.datum < vandaagInvoer()) f.datum = 'Die datum is al voorbij.'
  }
  return f
}

/** Alleen de velden van het schema, zonder wat de server erbij zet. */
export function veldenVan(t: TerugkerendeTaak | TerugkerendVelden): TerugkerendVelden {
  return {
    naam: t.naam, opdracht: t.opdracht, frequentie: t.frequentie, tijd: t.tijd, dagen: [...(t.dagen ?? [])],
    dagVanMaand: t.dagVanMaand, datum: t.datum, melden: t.melden, mailen: t.mailen,
  }
}

export const LAATST_STATUS: Record<NonNullable<TerugkerendeTaak['laatstStatus']>, { label: string; kleur: string }> = {
  klaar: { label: 'Gelukt', kleur: 'ok' },
  mislukt: { label: 'Mislukt', kleur: 'danger' },
  bezig: { label: 'Bezig', kleur: 'info' },
  overgeslagen: { label: 'Overgeslagen', kleur: 'warn' },
}
